import { useTranslation } from 'react-i18next'
import { investigationPath } from '@/application/seo/investigation-url'
import { useGetHomepageFeed } from '@/application/services/public-investigation.service'
import { useJsonLd } from '@/application/hooks/useJsonLd'
import { resolveLanguage } from '@/application/i18n/language'
import { localizedAbsoluteUrl } from '@/application/seo/site'
import { selectHomeSections } from './selectHomeSections'
import { HomeBreakingBar } from './components/HomeBreakingBar'
import { HomeLeadStory } from './components/HomeLeadStory'
import { HomeInvestigationList } from './components/HomeInvestigationList'
import { HomeSecondaryGrid } from './components/HomeSecondaryGrid'
import { HomeCategorySections } from './components/HomeCategorySections'
import { HomeLatestRiver } from './components/HomeLatestRiver'
import { HomeSkeleton } from './components/HomeSkeleton'

/**
 * The "news front page" homepage variant: breaking bar, lead story +
 * ongoing/completed investigation lists, secondary grid, category rails,
 * and the latest river. Composed from `useGetHomepageFeed` + the pure
 * `selectHomeSections` grouping.
 */
export function NewsHomePage() {
  const { t, i18n } = useTranslation()
  const lang = resolveLanguage(i18n.language)
  const { data: feed, isLoading } = useGetHomepageFeed()
  const sections = selectHomeSections(feed)

  useJsonLd(
    'ld-json-home-itemlist',
    feed
      ? {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: [...(sections.lead ? [sections.lead] : []), ...sections.headlines].map(
            (investigation, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              url: localizedAbsoluteUrl(investigationPath(investigation, lang), lang),
              name: investigation.name,
            }),
          ),
        }
      : null,
  )

  if (isLoading) return <HomeSkeleton />

  if (!sections.lead) {
    return (
      <div className="mx-auto w-full max-w-[100rem] bg-card grow 2xl:border-x 2xl:border-border px-4 py-16 text-center text-muted-foreground">
        {t('home.empty_state')}
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-[100rem] bg-card grow 2xl:border-x 2xl:border-border">
      <HomeBreakingBar />

      <div className="pb-8 space-y-10">
        {/*
         * `xl:` rather than `lg:`: below it, the sidebar column would only
         * get ~1/3 of a 1024-1279px viewport (~300-400px), too narrow for
         * the lists' title+thumbnail row — see HomeInvestigationList. Full
         * width single-column a bit longer keeps that row comfortable.
         */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-y-8 xl:gap-x-0">
          <div className="xl:col-span-2">
            <HomeLeadStory investigation={sections.lead} />
          </div>
          <div className="px-4 xl:pl-8 xl:pt-4 space-y-8">
            <HomeInvestigationList
              status="active"
              titleKey="home.headlines_title"
              excludeId={sections.lead.id}
            />
            <HomeInvestigationList status="inactive" titleKey="home.headlines_completed_title" />
          </div>
        </div>

        <div className="px-4 space-y-10">
          <HomeSecondaryGrid investigations={sections.river.slice(0, 3)} />
          <HomeCategorySections rails={sections.rails} />
          <HomeLatestRiver investigations={sections.river} />
        </div>
      </div>
    </div>
  )
}
